import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Tabs,
  Button,
  Space,
  Typography,
  Alert,
  Skeleton,
  Row,
  Col,
  Modal,
  message,
  Form,
  Input,
} from 'antd';
import {
  ArrowLeftOutlined,
  ReadOutlined,
  ReloadOutlined,
  LinkOutlined,
  CloudServerOutlined,
  ThunderboltOutlined,
  SettingOutlined,
  RobotOutlined,
  FilePdfOutlined,
  FileMarkdownOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { PageHeader } from '@/components/PageHeader';
import { StatusPill } from '@/components/StatusPill';
import { SectionCard } from '@/components/SectionCard';
import { KpiTile } from '@/components/KpiTile';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { SourceUploadDropzone } from './SourceUploadDropzone';
import { SourceTable } from './SourceTable';
import { AddLinkModal } from './AddLinkModal';
import { TestQueryPanel } from './TestQueryPanel';
import { LinkAgentsDrawer } from './LinkAgentsDrawer';

const { Text, Paragraph } = Typography;

// Single-KB workspace. Hero stats up top, tabbed body:
//   Sources       — drag-drop dropzone + unified PDFs/Markdown/Links table
//   Test query    — Azure AI Search hybrid retrieval preview
//   Linked agents — which agents ground responses in this KB
//   Settings      — title/description edit + RAG infra config (read-only)

export const KnowledgeDetailPage = observer(function KnowledgeDetailPage() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const navigate = useNavigate();
  const { knowledgeDetailStore: kb, ragStore: rag, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [addLinkOpen, setAddLinkOpen] = useState(false);
  const [linkAgentsOpen, setLinkAgentsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('sources');

  useEffect(() => {
    if (!Number.isFinite(id)) return;
    void kb.loadFor(id);
    rag.resetTest(id);
    return () => {
      // Don't clear: we want returning to feel instant. cleanup happens via reaction.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!Number.isFinite(id)) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<ReadOutlined />}
          title="Invalid knowledge base"
          description="The id in the URL is not a number."
          size="lg"
        />
      </SectionCard>
    );
  }

  const counts = kb.sourceCounts;
  const indexed = kb.indexedCounts;
  const indexedPct = indexed.total > 0 ? Math.round((indexed.indexed / indexed.total) * 100) : 0;

  const headerActions = (
    <Space>
      <Button
        icon={<LinkOutlined />}
        onClick={() => {
          setAddLinkOpen(true);
          trackEvent('Knowledge.AddLinkOpened', { knowledgeId: id });
        }}
      >
        Add link
      </Button>
      <Button
        icon={<ReloadOutlined />}
        onClick={() => kb.refresh()}
        loading={kb.isLoading}
      >
        Refresh
      </Button>
    </Space>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/knowledge')}
          style={{ paddingLeft: 0, color: isDark ? colors.slate[300] : colors.slate[500] }}
        >
          All knowledge bases
        </Button>
      </div>

      <PageHeader
        accent="ink"
        icon={<ReadOutlined />}
        title={
          kb.knowledge?.title || (
            <span style={{ fontStyle: 'italic', opacity: 0.7 }}>
              {kb.isLoading ? 'Loading…' : '(untitled)'}
            </span>
          )
        }
        subtitle={
          kb.knowledge?.description ?? (counts.total === 0 ? 'No sources yet — drop files below.' : null)
        }
        count={counts.total}
        countLabel={counts.total === 1 ? 'source' : 'sources'}
        actions={headerActions}
      />

      {kb.error && (
        <Alert
          type={kb.knowledge ? 'warning' : 'error'}
          message={kb.knowledge ? 'Some content failed to load' : 'Failed to load this knowledge base'}
          description={kb.error}
          showIcon
          closable
        />
      )}

      {/* Hero KPIs */}
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} md={6}>
          <KpiTile
            label="PDFs"
            value={counts.pdf}
            hint={counts.pdf === 1 ? 'document' : 'documents'}
            icon={<FilePdfOutlined />}
            accent="ink"
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <KpiTile
            label="Markdown"
            value={counts.markdown}
            hint={counts.markdown === 1 ? 'note' : 'notes'}
            icon={<FileMarkdownOutlined />}
            accent="violet"
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <KpiTile
            label="Links"
            value={counts.link}
            hint={counts.link === 1 ? 'URL' : 'URLs'}
            icon={<LinkOutlined />}
            accent="amber"
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <KpiTile
            label="Indexed"
            value={
              <span>
                {indexed.indexed}
                <span
                  style={{
                    fontSize: 18,
                    color: isDark ? colors.slate[400] : colors.slate[500],
                    fontWeight: 500,
                    marginLeft: 4,
                  }}
                >
                  / {indexed.total}
                </span>
              </span>
            }
            hint={
              indexed.total === 0
                ? 'No sources yet'
                : indexed.pending > 0
                ? `${indexed.pending} pending — try refresh`
                : 'All sources are queryable'
            }
            icon={<CloudServerOutlined />}
            accent={indexed.total === 0 ? 'amber' : indexed.pending === 0 ? 'green' : 'amber'}
            trend={
              indexed.total > 0
                ? {
                    direction: indexed.pending === 0 ? 'up' : 'flat',
                    label: `${indexedPct}%`,
                  }
                : undefined
            }
          />
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={(k) => {
          setActiveTab(k);
          trackEvent('Knowledge.TabChanged', { tab: k, knowledgeId: id });
        }}
        items={[
          {
            key: 'sources',
            label: (
              <span>
                <CloudServerOutlined /> Sources
              </span>
            ),
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <SourceUploadDropzone />
                <SourceTable />
              </div>
            ),
          },
          {
            key: 'test',
            label: (
              <span>
                <ThunderboltOutlined /> Test query
              </span>
            ),
            children: <TestQueryPanel knowledgeId={id} />,
          },
          {
            key: 'agents',
            label: (
              <span>
                <RobotOutlined /> Linked agents
              </span>
            ),
            children: <LinkedAgentsTab onLinkAgents={() => setLinkAgentsOpen(true)} />,
          },
          {
            key: 'settings',
            label: (
              <span>
                <SettingOutlined /> Settings
              </span>
            ),
            children: <SettingsTab />,
          },
        ]}
      />

      <AddLinkModal open={addLinkOpen} onClose={() => setAddLinkOpen(false)} />

      <LinkAgentsDrawer
        open={linkAgentsOpen}
        onClose={() => setLinkAgentsOpen(false)}
        // Pre-checked / hidden agents are derived from the same store state
        // the LinkedAgentsTab renders, so the drawer never offers an agent
        // that's already linked.
        alreadyLinkedIds={new Set(kb.linkedAgents.map((l) => l.aiAgentId))}
        knowledgeTitle={kb.knowledge?.title || `Knowledge #${id}`}
      />
    </div>
  );
});

// ── Linked agents tab ───────────────────────────────────────────────────────

interface LinkedAgentsTabProps {
  /** Opens the LinkAgentsDrawer — the canonical add-agent action. */
  onLinkAgents: () => void;
}

const LinkedAgentsTab = observer(function LinkedAgentsTab({ onLinkAgents }: LinkedAgentsTabProps) {
  const { knowledgeDetailStore: kb, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  // Render from kb.linkedAgents (AiAgentKnowledge link rows) instead of the
  // older kb.knowledge?.agents projection, because (a) we need the link row id
  // to drive Unlink, and (b) the rows now come back with the full Agent nav
  // populated by AiAgentKnowledgeRepository's projection.
  const links = kb.linkedAgents;

  async function handleUnlink(linkId: number, agentName: string): Promise<void> {
    Modal.confirm({
      title: 'Unlink agent from this knowledge base?',
      content: (
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>{agentName}</Text> will no longer retrieve grounding chunks from this KB at
          runtime. The agent and its other knowledge sources are unaffected. You can re-link any
          time.
        </Paragraph>
      ),
      okText: 'Unlink',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await kb.unlinkAgent(linkId);
          message.success(`Unlinked ${agentName}`);
          trackEvent('AgentKnowledge.UnlinkedFromKb', {
            knowledgeId: kb.knowledgeId ?? -1,
            linkId,
          });
        } catch (err) {
          message.error(err instanceof Error ? err.message : 'Failed to unlink agent');
        }
      },
    });
  }

  if (kb.isLoading && links.length === 0) {
    return (
      <SectionCard>
        <Skeleton active paragraph={{ rows: 3 }} />
      </SectionCard>
    );
  }

  if (links.length === 0) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<RobotOutlined />}
          title="No agents linked yet"
          description="Link an agent so its responses can quote authoritative material from this knowledge base. The agent will retrieve grounding chunks from these sources at runtime."
          size="md"
          action={
            <Button type="primary" icon={<LinkOutlined />} onClick={onLinkAgents}>
              Link agents
            </Button>
          }
        />
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Agents grounded in this knowledge base"
      subtitle="At runtime, each agent below queries this KB via hybrid search (vector + BM25 + semantic rerank) and injects the top chunks into the prompt context."
      actions={
        <Button type="primary" icon={<LinkOutlined />} onClick={onLinkAgents}>
          Link agents
        </Button>
      }
    >
      <div
        style={{
          // Single-column list — full agent display names fit on one line.
          // Linked-agent count per KB is small (rarely > 10), so vertical
          // scroll cost is negligible. Mirrors the same fix on the Skills
          // page's LinkedAgentsPanel.
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginTop: 8,
        }}
      >
        {links.map((link) => {
          const a = link.agent;
          const displayName = a?.displayName ?? `Agent #${link.aiAgentId}`;
          const slug = a?.canonicalName ?? a?.a2AAgentName ?? null;
          // Audience tags — mirror the agent header chip styling.
          const audienceTags: { label: string; show: boolean }[] = [
            { label: 'Patient', show: !!a?.patientApp },
            { label: 'Care team', show: !!a?.merchantApp },
          ].filter((t) => t.show);
          return (
            <div
              key={link.id}
              style={{
                border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                borderRadius: 10,
                padding: '12px 14px',
                background: isDark ? colors.slate[800] : '#FFFFFF',
                transition: 'border-color 120ms ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: isDark ? `${colors.ink[700]}55` : colors.ink[50],
                    color: colors.ink[600],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    flexShrink: 0,
                  }}
                >
                  <RobotOutlined />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: isDark ? colors.slate[50] : colors.slate[900],
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {displayName}
                  </div>
                  {slug && (
                    <div
                      style={{
                        fontSize: 11.5,
                        fontFamily: "'JetBrains Mono', monospace",
                        color: isDark ? colors.slate[400] : colors.slate[500],
                        marginTop: 2,
                      }}
                    >
                      {slug}
                    </div>
                  )}
                  {(audienceTags.length > 0 || a?.enableSkillRouter) && (
                    <Space size={6} wrap style={{ marginTop: 6 }}>
                      {audienceTags.map((t) => (
                        <StatusPill key={t.label} tone="slate">{t.label}</StatusPill>
                      ))}
                      {a?.enableSkillRouter && <StatusPill tone="violet">Router on</StatusPill>}
                    </Space>
                  )}
                </div>
                <Button
                  size="small"
                  danger
                  type="text"
                  onClick={() => void handleUnlink(link.id, displayName)}
                >
                  Unlink
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
});

// ── Settings tab (metadata + RAG config) ─────────────────────────────────────

const SettingsTab = observer(function SettingsTab() {
  const { knowledgeDetailStore: kb, ragStore: rag, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm<{ title: string; description?: string }>();

  function startEdit(): void {
    if (!kb.knowledge) return;
    form.setFieldsValue({
      title: kb.knowledge.title ?? '',
      description: kb.knowledge.description ?? '',
    });
    setEditing(true);
  }

  async function handleSave(): Promise<void> {
    try {
      const values = await form.validateFields();
      await kb.updateMetadata({
        title: values.title?.trim(),
        description: values.description?.trim() || undefined,
      });
      message.success('Knowledge base updated');
      setEditing(false);
    } catch (err: unknown) {
      const isFormError = err && typeof err === 'object' && 'errorFields' in err;
      if (!isFormError) {
        message.error(err instanceof Error ? err.message : 'Failed to update');
      }
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SectionCard
        title="Metadata"
        subtitle="Surfaced in citations, agent admin, and search."
        actions={
          !editing ? (
            <Button size="small" icon={<EditOutlined />} onClick={startEdit}>
              Edit
            </Button>
          ) : (
            <Space>
              <Button size="small" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button size="small" type="primary" onClick={handleSave}>
                Save
              </Button>
            </Space>
          )
        }
      >
        {editing ? (
          <Form form={form} layout="vertical">
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, max: 300 }]}
            >
              <Input maxLength={300} showCount />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={3} maxLength={500} showCount />
            </Form.Item>
          </Form>
        ) : (
          <DetailGrid
            isDark={isDark}
            rows={[
              { label: 'Title', value: kb.knowledge?.title ?? '—' },
              { label: 'Description', value: kb.knowledge?.description ?? '—' },
              {
                label: 'Created',
                value: kb.knowledge?.createdDate
                  ? new Date(kb.knowledge.createdDate).toLocaleString()
                  : '—',
              },
              {
                label: 'Last updated',
                value: kb.knowledge?.lastUpdatedDate
                  ? new Date(kb.knowledge.lastUpdatedDate).toLocaleString()
                  : '—',
              },
              { label: 'KB id', value: kb.knowledge?.id != null ? `#${kb.knowledge.id}` : '—' },
            ]}
          />
        )}
      </SectionCard>

      <SectionCard
        title={
          <span>
            <CloudServerOutlined style={{ marginRight: 6 }} />
            RAG infrastructure
          </span>
        }
        subtitle="Read-only — managed at the merchant level. This is the Azure AI Search index + embedding model that backs every KB in this merchant."
      >
        {rag.isLoadingConfig && !rag.config ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : rag.configError ? (
          <Alert type="warning" showIcon message={rag.configError} closable />
        ) : (
          <DetailGrid
            isDark={isDark}
            rows={[
              {
                label: 'Azure AI Search endpoint',
                value: rag.config?.searchEndpoint ?? '—',
                mono: true,
              },
              {
                label: 'Embedding model',
                value: rag.config?.textEmbeddingGenerationModel ?? '—',
                mono: true,
              },
              {
                label: 'Generation model',
                value: rag.config?.textGenerationModel ?? '—',
                mono: true,
              },
              {
                label: 'Search key',
                value: rag.config?.searchKey ? '••••••••' + rag.config.searchKey.slice(-4) : '—',
                mono: true,
              },
              {
                label: 'OpenAI key',
                value: rag.config?.openAiKey ? '••••••••' + rag.config.openAiKey.slice(-4) : '—',
                mono: true,
              },
            ]}
          />
        )}
      </SectionCard>

      <SectionCard
        title={
          <span style={{ color: colors.red[600] }}>
            Danger zone
          </span>
        }
      >
        <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 12 }}>
          Deleting this knowledge base removes all PDFs, Markdown sources, and links. Agents
          currently grounded in this KB will lose access to its sources.
        </Paragraph>
        <Button
          danger
          onClick={() => {
            Modal.confirm({
              title: 'Delete knowledge base?',
              content:
                'This action removes all sources and is not reversible. Proceed?',
              okText: 'Delete',
              okButtonProps: { danger: true },
              onOk: async () => {
                if (kb.knowledgeId == null) return;
                try {
                  // The list store handles removal so the list view stays consistent.
                  const root = (kb as unknown as { root?: { knowledgeStore?: { remove: (id: number) => Promise<void> } } }).root;
                  if (root?.knowledgeStore) {
                    await root.knowledgeStore.remove(kb.knowledgeId);
                  }
                  message.success('Knowledge base deleted');
                  window.history.back();
                } catch (err) {
                  message.error(err instanceof Error ? err.message : 'Delete failed');
                }
              },
            });
          }}
        >
          Delete this knowledge base
        </Button>
      </SectionCard>
    </div>
  );
});

// ── Tiny detail-grid primitive ──────────────────────────────────────────────

interface DetailGridRow {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}

function DetailGrid({ rows, isDark }: { rows: DetailGridRow[]; isDark: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {rows.map((r, i) => (
        <div
          key={r.label}
          style={{
            display: 'grid',
            gridTemplateColumns: '180px 1fr',
            gap: 12,
            padding: '8px 0',
            borderBottom:
              i < rows.length - 1
                ? `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`
                : 'none',
          }}
        >
          <Text
            style={{
              fontSize: 12,
              color: isDark ? colors.slate[400] : colors.slate[500],
              fontWeight: 500,
            }}
          >
            {r.label}
          </Text>
          <Text
            style={{
              color: isDark ? colors.slate[100] : colors.slate[800],
              fontFamily: r.mono ? "'JetBrains Mono', monospace" : undefined,
              fontSize: r.mono ? 12 : 13,
              wordBreak: 'break-all',
            }}
          >
            {r.value}
          </Text>
        </div>
      ))}
    </div>
  );
}
